import { createRouter, createWebHistory } from 'vue-router';

import store from '@/store';

import Navigation from '@/models/navigation';
import System from '@/models/system';
import User from '@/models/user';

import HomeIndex from '@/pages/home/Index.vue';

import BrowseClaim from '@/pages/claim/Browse.vue';
import SearchClaim from '@/pages/claim/Search.vue';
import ViewClaim from '@/pages/claim/View.vue';
import ViewClaimHistory from '@/pages/claim/ViewHistory.vue';

import BrowseCover from '@/pages/cover/Browse.vue';
import SearchCover from '@/pages/cover/Search.vue';
import ViewCover from '@/pages/cover/View.vue';
import ViewCoverPublic from '@/pages/cover/Public.vue';
import CreateCover from '@/pages/cover/Create.vue';
import RenewCover from '@/pages/cover/Renew.vue';
import EditCover from '@/pages/cover/Edit.vue';

import Login from '@/pages/auth/Login.vue';
import TFACode from '@/pages/auth/TFACode.vue';
import TFAConfigure from '@/pages/auth/TFAConfigure.vue';

import Password from '@/pages/auth/password/Index.vue';
import Forgot from '@/pages/auth/password/Forgot.vue';
import Reset from '@/pages/auth/password/Reset.vue';

import UserSettings from '@/pages/users/Settings.vue';

import Associations from '@/pages/associations/Index.vue';
import BrowseAssociations from '@/pages/associations/Browse.vue';
import EditAssociations from '@/pages/associations/Edit.vue';

import Companies from '@/pages/companies/Index.vue';
import BrowseCompanies from '@/pages/companies/Browse.vue';
import EditCompanies from '@/pages/companies/Edit.vue';

import EditAdmin from '@/pages/admin/Edit.vue';

import BrowseReports from '@/pages/reports/Browse.vue';

import NotFound from '@/pages/errors/NotFound.vue';

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            name: 'home',
            component: HomeIndex,
            meta: {
                navlink: 'home',
            },
        },
        {
            path: '/claims',
            name: 'home-claims',
            component: SearchClaim,
            meta: {
                navlink: 'claim',
            },
        },
        {
            path: '/claims/search/:id',
            name: 'home-claims-search',
            component: BrowseClaim,
            meta: {
                navlink: 'claim',
            },
        },
        {
            path: '/claims/:id',
            name: 'view-claim',
            component: ViewClaim,
            meta: {
                navlink: 'claim',
            },
        },
        {
            path: '/claims/history/:id',
            name: 'view-claim-history',
            component: ViewClaimHistory,
            meta: {
                navlink: 'claim',
            },
        },
        {
            path: '/covers',
            name: 'home-cover',
            component: SearchCover,
            meta: {
                navlink: 'cover',
            },
        },
        {
            path: '/covers/search',
            name: 'home-cover-search-all',
            component: BrowseCover,
            meta: {
                navlink: 'cover',
            },
        },
        {
            path: '/covers/search/:id',
            name: 'home-cover-search',
            component: BrowseCover,
            meta: {
                navlink: 'cover',
            },
        },
        {
            path: '/covers/create',
            name: 'create-cover',
            component: CreateCover,
            meta: {
                navlink: 'cover',
            },
        },
        {
            path: '/covers/renew',
            name: 'renew-cover',
            component: RenewCover,
            meta: {
                navlink: 'cover',
            },
        },
        {
            path: '/covers/edit/:id',
            name: 'edit-cover',
            component: EditCover,
            meta: {
                navlink: 'cover',
            },
        },
        {
            path: '/covers/:code/public',
            name: 'view-cover-public',
            component: ViewCoverPublic,
            meta: {
                navlink: 'cover',
            },
        },
        {
            path: '/covers/view/:id',
            name: 'view-cover',
            component: ViewCover,
            meta: {
                navlink: 'cover',
            },
        },
        {
            path: '/login',
            name: 'login',
            component: Login,
        },
        {
            path: '/tfa/code',
            name: 'tfa-code',
            component: TFACode,
        },
        {
            path: '/tfa/configure',
            name: 'tfa-configure',
            component: TFAConfigure,
        },
        {
            path: '/password',
            component: Password,
            children: [
                {
                    path: 'forgot',
                    name: 'forgot_password',
                    component: Forgot,
                },
                {
                    path: 'reset/:token?',
                    name: 'reset',
                    component: Reset,
                },
            ],
        },
        {
            path: '/settings',
            name: 'user-settings',
            component: UserSettings,
            meta: {
                navlink: 'user',
            },
        },
        {
            path: '/settings/:tab',
            name: 'user-settings-tab',
            component: UserSettings,
            meta: {
                navlink: 'user',
            },
        },
        {
            path: '/associations',
            component: Associations,
            children: [
                {
                    path: '',
                    name: 'browse-associations',
                    component: BrowseAssociations,
                    meta: {
                        access: {
                            abilities: ['browse-associations'],
                        },
                    },
                },
                {
                    path: ':id',
                    name: 'edit-associations',
                    component: EditAssociations,
                    meta: {
                        navlink: 'association',
                        access: {
                            abilities: ['manage-association'],
                        },
                    },
                },
                {
                    path: ':id/:tab',
                    name: 'edit-associations-tab',
                    component: EditAssociations,
                    meta: {
                        navlink: 'association',
                        access: {
                            abilities: ['manage-association'],
                        },
                    },
                },
            ],
        },
        {
            path: '/companies',
            component: Companies,
            children: [
                {
                    path: '',
                    name: 'browse-companies',
                    component: BrowseCompanies,
                    meta: {
                        access: {
                            abilities: ['browse-companies'],
                        },
                    },
                },
                {
                    path: ':id',
                    name: 'edit-companies',
                    component: EditCompanies,
                    meta: {
                        navlink: 'company',
                        access: {
                            abilities: ['manage-company'],
                        },
                    },
                },
                {
                    path: ':id/:tab',
                    name: 'edit-companies-tab',
                    component: EditCompanies,
                    meta: {
                        navlink: 'company',
                        access: {
                            abilities: ['manage-company'],
                        },
                    },
                },
            ],
        },
        {
            path: '/admin',
            name: 'edit-admin',
            component: EditAdmin,
            meta: {
                navlink: 'admin',
                access: {
                    abilities: ['manage-admin'],
                },
            },
        },
        {
            path: '/admin/:tab',
            name: 'edit-admin-tab',
            component: EditAdmin,
            meta: {
                navlink: 'admin',
                access: {
                    abilities: ['manage-admin'],
                },
            },
        },
        {
            path: '/reports',
            name: 'browse-reports',
            component: BrowseReports,
            meta: {
                navlink: 'reports',
            },
        },
        {
            // will match everything
            path: '/:pathMatch(.*)*',
            name: 'not-found',
            component: NotFound,
        },
        {
            // will match everything bad
            path: '/:pathMatch(.*)',
            name: 'bad-not-found',
            component: NotFound,
        },
    ],
});

router.beforeEach((to, from) => {
    axios
        .get('/api/me')
        .then((response) => {
            const is_user_logged_in = response.data.data.is_logged_in;

            User.setUserInfo(response.data.data);
            return handleNavigation(to, from, is_user_logged_in);
        })
        .catch((error) => {
            if (error.response.status == 401) {
                System.resetData();

                return handleNavigation(to, from, false);
            }
        })
        .finally(() => {
            store.dispatch('system/updateInitialized', true);
        });
});

const handleNavigation = (to, from, is_user_logged_in) => {
    // Before route enter, preload all locale files described in route meta
    const nav = (to.meta || {}).navlink || 'home';

    Navigation.setCurrentLink(nav);

    // navigating regular routes
    if (to.name !== 'login') {
        if (!is_user_logged_in) {
            if (to.name === 'forgot_password' || to.name === 'view-cover-public') {
                return;
            } else if (to.name === 'reset') {
                if (to.params.token == '' || to.params.token == undefined) {
                    return { name: 'login', params: { nextUrl: to.fullPath } };
                } else {
                    return;
                }
            } else {
                return { name: 'login', params: { nextUrl: to.fullPath } };
            }
        } else if (to.name === 'reset') {
            if (store.state.user.is_change_password) {
                return;
            } else {
                return { name: 'home' };
            }
        } else {
            if (to.name === 'create-cover' || to.name === 'renew-cover') {
                if (
                    store.state.user.abilities.includes('add-covers') &&
                    (store.state.user.abilities.includes('manage-association') ||
                        store.state.user.abilities.includes('manage-admin'))
                ) {
                    return { name: 'not-found' };
                } else if (store.state.user.abilities.includes('add-covers')) {
                    return;
                } else {
                    return { name: 'not-found' };
                }
            }

            let is_tfa_enabled =
                store.state.user.is_tfa_enabled ||
                store.state.user.is_company_tfa_enabled ||
                store.state.user.is_association_tfa_enabled;

            // if two factor authentication is enabled and user logged in but haven't entered their code,
            // stop the user from moving from the code page
            // if the tfa is enabled (whether by company or association) but not configured for user, redirect them to configure it.
            if (is_tfa_enabled && !store.state.user.tfa_processed && store.state.user.tfa_type == null) {
                if (to.name == 'tfa-configure') {
                    return;
                } else {
                    return { name: 'tfa-configure' };
                }
            } else if (is_tfa_enabled && !store.state.user.tfa_processed) {
                if (to.name == 'tfa-code') {
                    return;
                } else {
                    return { name: 'tfa-code' };
                }
            } else if (store.state.user.is_change_password) {
                return { name: 'reset' };
            } else {
                if (to.name == 'tfa-code' || to.name == 'tfa-configure') {
                    return { name: 'home' };
                } else {
                    let has_access = checkModuleNav(to.name);

                    // if the route describes access levels and the user store does not have a truthy value found, navigate to not found
                    if (
                        to.meta.access !== undefined &&
                        to.meta.access.abilities !== undefined &&
                        to.meta.access.abilities.length >= 1
                    ) {
                        _.forEach(to.meta.access.abilities, function (ability) {
                            if (!User.can(ability)) has_access = false;
                        });
                    }

                    if (has_access) {
                        return;
                    } else {
                        return { name: 'not-found' };
                    }
                }
            }
        }
    } else {
        // navigating to login route
        if (is_user_logged_in) {
            return { name: 'home' };
        } else {
            return;
        }
    }
};

const checkModuleNav = (nav_name) => {
    let has_access = true;

    if (store.state.user.module_state) {
        if (['home-claims', 'home-claims-search', 'view-claim'].includes(nav_name)) {
            if (!store.state.user.modules.claimsvault) {
                has_access = false;
            }
        }
    }

    if (
        ['home-cover', 'home-cover-search', 'home-cover-search-all', 'view-cover'].includes(nav_name) &&
        !store.state.user.modules.covervault
    ) {
        has_access = false;
    }

    if (nav_name == 'view-cover-public' && !store.state.system.is_epicvault_enabled) {
        has_access = false;
    }

    return has_access;
};

export default router;
